import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAppUrl } from '../utils/navigationHelper';
import { 
  BarChart3, Shield, Zap, Database, CheckCircle2, Clock,
  MapPin, TrendingUp, ChevronRight, Target, Brain, Users,
  LineChart, ArrowRight, Globe, Clipboard, Layers, 
  FileSearch, Award, Sparkles, Monitor, ArrowUpRight,
  Code2, Smartphone
} from 'lucide-react';
import logoSource from '../assets/noble_syntax_home.png';
import dataQuadImageSource from '../assets/dataquadio.png';
import wbgLogo from '../assets/wbg.png';
import saiparLogo from '../assets/saipar.png';
import ipaLogo from '../assets/ipa.jpg';
import vvobLogo from '../assets/vvob.png';
import './Home.css';

// ============================================================================
// SCROLL ANIMATION HOOK
// ============================================================================
const useScrollReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold, rootMargin: '-40px' }
    );
    observer.observe(node);
    return () => observer.unobserve(node);
  }, [threshold]);

  return [ref, isVisible];
};

// ============================================================================
// ANIMATED COUNTER
// ============================================================================
const AnimatedCounter = ({ end, suffix = '', duration = 2000, isVisible }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!isVisible) return;
    const startTime = Date.now();
    const numericEnd = parseFloat(end);
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * numericEnd));
      if (progress < 1) requestAnimationFrame(animate);
      else setCount(numericEnd);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span>{count.toLocaleString()}{suffix}</span>;
};

// ============================================================================
// NAVBAR
// ============================================================================
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToSection = (id) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };
  
  return (
    <nav className={`ns-navbar${scrolled ? ' scrolled' : ''}`}>
      <div className="ns-nav-inner">
        <a href="/" className="ns-nav-logo">
          <img src={logoSource} alt="Noble Syntax" />
        </a>

        <ul className={`ns-nav-links${mobileOpen ? ' open' : ''}`}>
          <li><button onClick={() => scrollToSection('dataquad')}>DataQuad</button></li>
          <li><button onClick={() => scrollToSection('services')}>Services</button></li>
          <li><button onClick={() => scrollToSection('technology')}>Solutions</button></li>
          {/* NobleLearn is a static page in /public, outside the SPA router,
              so navigate with a full page load rather than scrollToSection */}
          <li><button onClick={() => { window.location.href = '/noblesyntaxlearn/'; }}>NobleSyntaxLearn</button></li>
          <li><button onClick={() => scrollToSection('impact')}>Impact</button></li>
          <li><button onClick={() => scrollToSection('enumerators')}>We're Hiring</button></li>
          {/*<li><button onClick={() => navigate('/careers/enumerators')}>We're Hiring</button></li>*/}
          {/*<li><button onClick={() => scrollToSection('contact')}>Contact</button></li>*/}
          <li className="ns-nav-cta-li">
            <a href={getAppUrl('/')} className="ns-nav-cta">Get Started</a>
          </li>
        </ul>

        <button
          className="ns-hamburger"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menu"
        >
          <span style={mobileOpen ? { transform: 'rotate(45deg) translate(5px, 5px)' } : {}} />
          <span style={mobileOpen ? { opacity: 0 } : {}} />
          <span style={mobileOpen ? { transform: 'rotate(-45deg) translate(5px, -5px)' } : {}} />
        </button>
      </div>
    </nav>
  );
};

// ============================================================================
// SECTION HEADER
// ============================================================================
const SectionHeader = ({ label, title, subtitle, align = 'center' }) => {
  const [ref, vis] = useScrollReveal();
  return (
    <div ref={ref} className={`ns-section-header ${vis ? 'ns-revealed' : ''}`} style={{ textAlign: align }}>
      {label && <span className="ns-section-label">{label}</span>}
      <h2 className="ns-section-title">{title}</h2>
      {subtitle && <p className="ns-section-subtitle">{subtitle}</p>}
    </div>
  );
};

const EnumeratorRecruitmentBanner = () => {
  const navigate = useNavigate();
  const [ref, isVisible] = useScrollReveal(0.15);  // reuse existing hook from Home.js
  const [hovered, setHovered] = useState(false);
 
  return (
    <section
      ref={ref}
      style={{
        padding: '80px 24px',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(160deg, #010814 0%, #050E1F 55%, #010814 100%)',
      }}
    >
      {/* ── BACKGROUND GRID TEXTURE ─────────────────────────────────────── */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(56,182,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(56,182,255,0.04) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 100%)',
        }}
      />
 
      {/* ── GLOW ORBS ───────────────────────────────────────────────────── */}
      <div aria-hidden style={{
        position: 'absolute', width: '500px', height: '500px',
        borderRadius: '50%', top: '-120px', left: '-100px',
        background: 'radial-gradient(circle, rgba(43,138,255,0.07) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <div aria-hidden style={{
        position: 'absolute', width: '400px', height: '400px',
        borderRadius: '50%', bottom: '-80px', right: '-60px',
        background: 'radial-gradient(circle, rgba(56,182,255,0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
 
      {/* ── MAIN CARD ───────────────────────────────────────────────────── */}
      <div
        style={{
          maxWidth: '880px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}
      >
        {/* Card shell */}
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(56,182,255,0.06) 0%, rgba(10,16,30,0.9) 50%, rgba(43,138,255,0.04) 100%)',
            border: '1px solid rgba(56,182,255,0.18)',
            borderRadius: '20px',
            padding: 'clamp(32px, 5vw, 56px)',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 0 60px rgba(56,182,255,0.05), 0 24px 80px rgba(0,0,0,0.5)',
            cursor: 'default',
          }}
        >
          {/* Corner accent — top-right geometric decoration */}
          <div aria-hidden style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '220px',
            height: '220px',
            background: 'radial-gradient(circle at top right, rgba(56,182,255,0.12) 0%, transparent 70%)',
            borderRadius: '0 20px 0 0',
            pointerEvents: 'none',
          }} />
 
          {/* Animated top-border accent line */}
          <div aria-hidden style={{
            position: 'absolute',
            top: 0,
            left: '10%',
            right: '10%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, #38B6FF, #4FC3F7, transparent)',
            opacity: 0.6,
          }} />
 
          {/* ── INNER LAYOUT: LEFT TEXT + RIGHT ACTION ── */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '40px',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
 
            {/* LEFT: Tag + Title + Details */}
            <div style={{ flex: 1, minWidth: '280px' }}>
              {/* Hiring badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(56,182,255,0.1)',
                border: '1px solid rgba(56,182,255,0.25)',
                borderRadius: '100px',
                padding: '5px 14px 5px 10px',
                marginBottom: '22px',
              }}>
                <span style={{
                  display: 'inline-block',
                  width: '8px', height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#38B6FF',
                  boxShadow: '0 0 10px rgba(56,182,255,0.9)',
                  animation: 'nsPulse 2s ease-in-out infinite',
                }} />
                <span style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  color: '#4FC3F7',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}>
                  Now Hiring · Applications Open
                </span>
              </div>
 
              {/* Title */}
              <h3 style={{
                fontSize: 'clamp(22px, 3.5vw, 34px)',
                fontWeight: '800',
                color: '#EDF4FF',
                margin: '0 0 14px',
                lineHeight: 1.15,
                letterSpacing: '-0.025em',
              }}>
                Field Enumerators<br />
                <span style={{
                  backgroundImage: 'linear-gradient(90deg, #38B6FF, #4FC3F7)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  Wanted in Zambia
                </span>
              </h3>
 
              {/* Short description */}
              <p style={{
                fontSize: '15px',
                color: '#7A8FAD',
                margin: '0 0 28px',
                lineHeight: 1.65,
                maxWidth: '420px',
              }}>
                We are recruiting skilled, motivated field enumerators for 
                upcoming research projects across the country.
                Training, tools, and supervision are provided.
              </p>
 
              {/* Pill tags */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
              }}>
                {[
                  { emoji: '📍', text: 'Zambia' },
                  { emoji: '⏱️', text: 'Short-term contract' },
                  { emoji: '🎓', text: 'Training provided' },
                ].map(({ emoji, text }) => (
                  <span
                    key={text}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '6px',
                      padding: '5px 12px',
                      fontSize: '12px',
                      color: '#9AAFCC',
                      fontWeight: '500',
                    }}
                  >
                    <span>{emoji}</span>
                    {text}
                  </span>
                ))}
              </div>
            </div>
 
            {/* RIGHT: Graphic + CTA */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '24px',
              minWidth: '200px',
            }}>
              {/* Decorative icon block */}
              <div style={{
                width: '96px',
                height: '96px',
                borderRadius: '22px',
                background: 'linear-gradient(135deg, rgba(56,182,255,0.15), rgba(43,138,255,0.08))',
                border: '1px solid rgba(56,182,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '40px',
                boxShadow: '0 0 30px rgba(56,182,255,0.08)',
              }}>
                📋
              </div>
 
              {/* CTA Button */}
              <button
                onClick={() => navigate('/careers/enumerators')}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: hovered
                    ? 'linear-gradient(135deg, #2B8AFF, #38B6FF)'
                    : 'linear-gradient(135deg, #38B6FF, #4FC3F7)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '15px 28px',
                  fontSize: '15px',
                  fontWeight: '700',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  letterSpacing: '-0.01em',
                  boxShadow: hovered
                    ? '0 8px 32px rgba(56,182,255,0.45)'
                    : '0 4px 20px rgba(56,182,255,0.3)',
                  transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                Apply Now
                <ArrowUpRight size={17} />
              </button>
 
              <span style={{
                fontSize: '12px',
                color: '#3A5A7A',
                textAlign: 'center',
                lineHeight: 1.5,
              }}>
                Takes ~10 minutes.<br />
                Applications close when positions are filled.
              </span>
            </div>
          </div>
        </div>
      </div>
 
      {/* Keyframe injection for pulse animation */}
      <style>{`
        @keyframes nsPulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 10px rgba(56,182,255,0.9); }
          50% { opacity: 0.5; box-shadow: 0 0 4px rgba(56,182,255,0.4); }
        }
      `}</style>
    </section>
  );
};


// ============================================================================
// FREE TUTORIAL BANNER - Data Quality Management for Field Surveys
// The tutorial is a static HTML file. It MUST live under /public (not /src):
// everything in /public is copied into the build as-is and served at its own
// URL, while /src files only exist if imported through webpack, which does
// not handle .html. Current location: public/noblesyntaxlearn/
// ============================================================================
const NOBLELEARN_DQM_URL = '/noblesyntaxlearn/data-quality-management-tutorial.html';

const DataQualityTutorialBanner = () => {
  const [ref, isVisible] = useScrollReveal(0.15);
  const [hovered, setHovered] = useState(false);

  return (
    <section
      ref={ref}
      style={{
        padding: '80px 24px',
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(160deg, #010814 0%, #050E1F 55%, #010814 100%)',
      }}
    >
      {/* Background grid texture */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(56,182,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(56,182,255,0.04) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 100%)',
        }}
      />

      {/* Glow orbs */}
      <div aria-hidden style={{
        position: 'absolute', width: '500px', height: '500px',
        borderRadius: '50%', top: '-120px', right: '-100px',
        background: 'radial-gradient(circle, rgba(43,138,255,0.07) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <div aria-hidden style={{
        position: 'absolute', width: '400px', height: '400px',
        borderRadius: '50%', bottom: '-80px', left: '-60px',
        background: 'radial-gradient(circle, rgba(56,182,255,0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Main card */}
      <div
        style={{
          maxWidth: '880px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(56,182,255,0.06) 0%, rgba(10,16,30,0.9) 50%, rgba(43,138,255,0.04) 100%)',
            border: '1px solid rgba(56,182,255,0.18)',
            borderRadius: '20px',
            padding: 'clamp(32px, 5vw, 56px)',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 0 60px rgba(56,182,255,0.05), 0 24px 80px rgba(0,0,0,0.5)',
            cursor: 'default',
          }}
        >
          {/* Corner accent */}
          <div aria-hidden style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '220px',
            height: '220px',
            background: 'radial-gradient(circle at top left, rgba(56,182,255,0.12) 0%, transparent 70%)',
            borderRadius: '20px 0 0 0',
            pointerEvents: 'none',
          }} />

          {/* Animated top-border accent line */}
          <div aria-hidden style={{
            position: 'absolute',
            top: 0,
            left: '10%',
            right: '10%',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, #38B6FF, #4FC3F7, transparent)',
            opacity: 0.6,
          }} />

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '40px',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>

            {/* LEFT: Tag + Title + Details */}
            <div style={{ flex: 1, minWidth: '280px' }}>
              {/* Free tutorial badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(56,182,255,0.1)',
                border: '1px solid rgba(56,182,255,0.25)',
                borderRadius: '100px',
                padding: '5px 14px 5px 10px',
                marginBottom: '22px',
              }}>
                <span style={{
                  display: 'inline-block',
                  width: '8px', height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#38B6FF',
                  boxShadow: '0 0 10px rgba(56,182,255,0.9)',
                  animation: 'nsPulse 2s ease-in-out infinite',
                }} />
                <span style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  color: '#4FC3F7',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}>
                  Free Tutorial · Open Access
                </span>
              </div>

              {/* Title */}
              <h3 style={{
                fontSize: 'clamp(22px, 3.5vw, 34px)',
                fontWeight: '800',
                color: '#EDF4FF',
                margin: '0 0 14px',
                lineHeight: 1.15,
                letterSpacing: '-0.025em',
              }}>
                Data Quality Management<br />
                <span style={{
                  backgroundImage: 'linear-gradient(90deg, #38B6FF, #4FC3F7)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  for Field Surveys
                </span>
              </h3>

              {/* Short description */}
              <p style={{
                fontSize: '15px',
                color: '#7A8FAD',
                margin: '0 0 28px',
                lineHeight: 1.65,
                maxWidth: '440px',
              }}>
                An interactive walkthrough of the full quality system:
                smart forms, bench-testing, high-frequency checks, spot-checks,
                back-checks, and audio audits, with the thresholds and actions
                used on real research projects.
              </p>

              {/* Pill tags */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
              }}>
                {[
                  { emoji: '🖱️', text: 'Interactive & animated' },
                  { emoji: '⏱️', text: 'About 20 minutes' },
                  { emoji: '🆓', text: 'No sign-up required' },
                ].map(({ emoji, text }) => (
                  <span
                    key={text}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '6px',
                      padding: '5px 12px',
                      fontSize: '12px',
                      color: '#9AAFCC',
                      fontWeight: '500',
                    }}
                  >
                    <span>{emoji}</span>
                    {text}
                  </span>
                ))}
              </div>
            </div>

            {/* RIGHT: Graphic + CTA */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '24px',
              minWidth: '200px',
            }}>
              {/* Decorative icon block */}
              <div style={{
                width: '96px',
                height: '96px',
                borderRadius: '22px',
                background: 'linear-gradient(135deg, rgba(56,182,255,0.15), rgba(43,138,255,0.08))',
                border: '1px solid rgba(56,182,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '40px',
                boxShadow: '0 0 30px rgba(56,182,255,0.08)',
              }}>
                🛡️
              </div>

              {/* CTA: plain anchor (not navigate) so the browser does a full page
                  load; the static file lives outside the SPA router */}
              <a
                href={NOBLELEARN_DQM_URL}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: hovered
                    ? 'linear-gradient(135deg, #2B8AFF, #38B6FF)'
                    : 'linear-gradient(135deg, #38B6FF, #4FC3F7)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '15px 28px',
                  fontSize: '15px',
                  fontWeight: '700',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  letterSpacing: '-0.01em',
                  textDecoration: 'none',
                  boxShadow: hovered
                    ? '0 8px 32px rgba(56,182,255,0.45)'
                    : '0 4px 20px rgba(56,182,255,0.3)',
                  transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                Open the Tutorial
                <ArrowUpRight size={17} />
              </a>

              <span style={{
                fontSize: '12px',
                color: '#3A5A7A',
                textAlign: 'center',
                lineHeight: 1.5,
              }}>
                Built on Innovations for Poverty Action's<br />
                public research protocols.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Keyframe injection for pulse animation */}
      <style>{`
        @keyframes nsPulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 10px rgba(56,182,255,0.9); }
          50% { opacity: 0.5; box-shadow: 0 0 4px rgba(56,182,255,0.4); }
        }
      `}</style>
    </section>
  );
};

// ============================================================================
// HOME COMPONENT
// ============================================================================
const Home = () => {
  const navigate = useNavigate();
  const [hoveredService, setHoveredService] = useState(null);
  const [activeFeature, setActiveFeature] = useState(0);
  const [impactRef, impactVisible] = useScrollReveal(0.3);

  // Load Outfit font
  useEffect(() => {
    document.title = 'Noble Syntax — Data & Technology Consultancy';
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    if (!document.querySelector('link[href*="Outfit"]')) {
      document.head.appendChild(link);
    }
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  // ---- DATA ----
  const services = [
    {
      icon: Clipboard,
      title: 'Data Collection',
      description: 'End-to-end survey design, enumerator recruitment, training, and field supervision. From household surveys to institutional assessments.',
      highlights: ['Enumerator recruitment & training', 'Field supervision & QA', 'Multi-regional deployment', 'KoBoToolbox & SurveyCTO'],
      color: '#0066FF',
      gradient: 'linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)',
    },
    {
      icon: FileSearch,
      title: 'Data Quality Management',
      description: 'Rigorous quality assurance protocols during and after data collection. Real-time monitoring, back-checks, and comprehensive validation pipelines.',
      highlights: ['Real-time quality monitoring', 'Back-check verification', 'Anomaly detection', 'Enumerator performance tracking'],
      color: '#7C3AED',
      gradient: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
    },
    {
      icon: BarChart3,
      title: 'Data Analysis & Visualization',
      description: 'From raw data to actionable insights. Statistical analysis, geospatial mapping, interactive dashboards, and publication-ready reports.',
      highlights: ['Statistical analysis', 'Geospatial data & mapping', 'Interactive dashboards', 'Custom reporting'],
      color: '#059669',
      gradient: 'linear-gradient(135deg, #059669 0%, #34D399 100%)',
    },
  ];

  const techSolutions = [
    {
      icon: Code2,
      title: 'Systems Development',
      description: 'Full-stack platforms built from the ground up. Architecture, backend APIs, databases, authentication, payments, and cloud deployment — designed, built, and maintained in-house.',
      highlights: ['Full-stack web platforms', 'Backend APIs & databases', 'Auth, payments & subscriptions', 'Cloud deployment & maintenance'],
      color: '#F97316',
      gradient: 'linear-gradient(135deg, #F97316 0%, #FBBF24 100%)',
      examples: [
        { label: 'DataQuad', href: 'https://dataquadapp.com', external: true },
      ],
    },
    {
      icon: Smartphone,
      title: 'App Development',
      description: 'Cross-platform desktop and mobile applications, from field-ready data tools to distribution through official app stores.',
      highlights: ['Desktop & mobile apps', 'Microsoft Store distribution', 'Offline-capable field tools', 'Secure sync & updates'],
      color: '#06B6D4',
      gradient: 'linear-gradient(135deg, #06B6D4 0%, #38BDF8 100%)',
      examples: [
        { label: 'DataQuad for Windows', href: 'https://dataquadapp.com/download', external: true },
      ],
    },
    {
      icon: Globe,
      title: 'Website Development',
      description: 'Fast, modern, search-optimized websites — from corporate sites to product landing pages, built to convert and easy to maintain.',
      highlights: ['Corporate & product websites', 'Responsive, modern design', 'SEO & performance optimization', 'Analytics & integrations'],
      color: '#EC4899',
      gradient: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)',
      examples: [
        { label: 'noblesyntax.com', href: '/', external: false },
        { label: 'dataquadapp.com', href: 'https://dataquadapp.com', external: true },
      ],
    },
  ];

  const features = [
    { 
      title: 'Completeness Analysis', 
      desc: 'Track missing data patterns across variables, collectors, and time periods with interactive heatmaps.',
      icon: CheckCircle2, color: '#10B981',
      screenshot: '/completeness_light.png'
    },
    { 
      title: 'Duplicate Detection', 
      desc: 'Identify duplicate entries with multi-variable pattern recognition and collector performance metrics.',
      icon: Layers, color: '#F59E0B',
      screenshot: '/uniqueness_light.png'
    },
    { 
      title: 'Outlier Detection', 
      desc: 'Statistical outlier identification using IQR and Z-score methods with variable-level quality scoring.',
      icon: Target, color: '#EF4444',
      screenshot: '/outliers_light.png'
    },
    { 
      title: 'Duration Analysis', 
      desc: 'Monitor interview duration patterns and flag suspiciously fast or slow responses.',
      icon: Clock, color: '#8B5CF6',
      screenshot: '/duration_light.png'
    },
    { 
      title: 'Survey Flow', 
      desc: 'Visualize response paths, track drop-off patterns, and analyze data collector consistency.',
      icon: LineChart, color: '#F97316',
      screenshot: '/surveyflow_light.png'
    },
    { 
      title: 'Geolocation Mapping', 
      desc: 'Validate GPS coordinates and visualize geographic distribution with interactive maps.',
      icon: MapPin, color: '#06B6D4',
      screenshot: '/geolocation_light.png'
    },
    { 
      title: 'Text Analysis', 
      desc: 'Analyze open-ended responses for quality, detect themes and clusters, and flag similar pairs.',
      icon: FileSearch, color: '#64748B',
      screenshot: '/textanalysis_light.png'
    },
    { 
      title: 'AI Quality Assessment', 
      desc: 'Machine learning-powered comprehensive quality scoring with anomaly detection.',
      icon: Brain, color: '#EC4899',
      screenshot: '/aidashboard_light.png'
    },
    { 
      title: 'Back-check Analysis', 
      desc: 'Compare primary and back-check surveys with weighted error scoring across Type 1, 2, and 3 variables.',
      icon: Clipboard, color: '#14B8A6',
      screenshot: '/backchecks_light.png'
    },
  ];

  const impactStats = [
    { value: '10000', suffix: '+', label: 'Surveys Analyzed', icon: Database },
    { value: '85', suffix: '%', label: 'Time Saved', icon: Clock },
    { value: '10', suffix: '+', label: 'Provinces Covered', icon: Globe },
    { value: '99', suffix: '.8%', label: 'Detection Accuracy', icon: Target },
  ];

  const trustedBy = [
    { name: 'SAIPAR', logo: saiparLogo },
    { name: 'IPA', logo: ipaLogo },
    { name: 'World Bank', logo: wbgLogo },
  ];

  return (
    <div className="ns-page">
      <Navbar />

      {/* ============ HERO ============ */}
      <section className="ns-hero">
        <div className="ns-hero-bg" />
        {/* Abstract animated data network */}
        <div className="ns-hero-visual" aria-hidden="true">
          <div className="ns-orb ns-orb-1" />
          <div className="ns-orb ns-orb-2" />
          <div className="ns-orb ns-orb-3" />
          <div className="ns-node ns-node-1" />
          <div className="ns-node ns-node-2" />
          <div className="ns-node ns-node-3" />
          <div className="ns-node ns-node-4" />
          <div className="ns-node ns-node-5" />
          <div className="ns-node ns-node-6" />
          <div className="ns-ring ns-ring-1" />
          <div className="ns-ring ns-ring-2" />
        </div>
        <div className="ns-hero-content">
          <div className="ns-hero-badge ns-fade-up">
            <Sparkles size={14} />
            <span>Data & Technology Consultancy</span>
          </div>
          <h1 className="ns-hero-title ns-fade-up ns-delay-1">
            Turning Raw Data Into<br />
            <span className="ns-hero-accent">Research-Grade</span> Insights
          </h1>
          <p className="ns-hero-subtitle ns-fade-up ns-delay-2">
            From survey design and field data collection to AI-powered quality assurance 
            and advanced analytics — we help organizations make confident, data-driven decisions.
          </p>
          <div className="ns-hero-actions ns-fade-up ns-delay-3">
            <a href={getAppUrl('/')} className="ns-btn-primary">
              Try DataQuad Free <ArrowRight size={18} />
            </a>
            <button onClick={() => document.getElementById('dataquad')?.scrollIntoView({ behavior: 'smooth' })} className="ns-btn-secondary">
              See How It Works
            </button>
          </div>

          {/* Founders Trusted By */}
          <div className="ns-hero-trust ns-fade-up ns-delay-4">
            <span className="ns-trust-label">Founders Footprints</span>
            <div className="ns-trust-logos">
              {trustedBy.map((org, i) => (
                <div key={i} className="ns-trust-item">
                  <img src={org.logo} alt={org.name} className="ns-trust-logo-img" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ DATAQUAD PLATFORM ============ */}
      <section id="dataquad" className="ns-dataquad-section">
        <div className="ns-dataquad-inner">
          <div className="ns-dataquad-left">
            <SectionHeader
              label="Our Platform"
              title="Meet DataQuad"
              subtitle="AI-powered survey data quality assessment platform. Connect KoBoToolbox, SurveyCTO, or ODK — and get instant, comprehensive quality analysis across 9 dimensions."
              align="left"
            />
            
            <div className="ns-dq-features-list">
              {features.map((f, i) => {
                const FIcon = f.icon;
                const isActive = activeFeature === i;
                return (
                  <button
                    key={i}
                    className={`ns-dq-feature-tab ${isActive ? 'active' : ''}`}
                    onClick={() => setActiveFeature(i)}
                  >
                    <div className="ns-dq-tab-icon" style={{ 
                      background: isActive ? f.color : 'rgba(0,0,0,0.04)',
                      color: isActive ? '#fff' : '#6B7280'
                    }}>
                      <FIcon size={18} />
                    </div>
                    <div className="ns-dq-tab-text">
                      <span className="ns-dq-tab-title">{f.title}</span>
                      {isActive && <span className="ns-dq-tab-desc">{f.desc}</span>}
                    </div>
                    {isActive && (
                      <div className="ns-dq-tab-progress" style={{ background: f.color }} />
                    )}
                  </button>
                );
              })}
            </div>

            <a href={getAppUrl('/')} className="ns-dq-cta">
              Start Free Trial <ArrowRight size={18} />
            </a>
          </div>

          <div className="ns-dataquad-right">
            <div className="ns-dq-preview-frame">
              <div className="ns-dq-preview-topbar">
                <div className="ns-dq-dots">
                  <span /><span /><span />
                </div>
                <div className="ns-dq-url-bar">
                  <Globe size={12} />
                  <span>dataquadapp.com</span>
                </div>
              </div>
              <div className="ns-dq-preview-img">
                <img 
                  src={features[activeFeature].screenshot} 
                  alt={features[activeFeature].title}
                  key={activeFeature}
                  className="ns-dq-screenshot"
                />
              </div>
            </div>
            <div className="ns-dq-brand-badge">
              <img src={dataQuadImageSource} alt="DataQuad" className="ns-dq-logo" />
              <div>
                <span className="ns-dq-brand-name">dataQuad</span>
                <span className="ns-dq-brand-tag">AI-Powered Quality Assessment</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section id="services" className="ns-services-section">
        <SectionHeader
          label="Our Services"
          title="End-to-End Data Services"
          subtitle="Beyond the platform — Noble Syntax provides comprehensive data solutions, from collecting it in the field to ensuring its quality and transforming it into actionable intelligence."
        />
        <div className="ns-services-grid">
          {services.map((service, i) => {
            const Icon = service.icon;
            const isHovered = hoveredService === i;
            return (
              <div
                key={i}
                className={`ns-service-card ns-reveal-card`}
                style={{ '--card-delay': `${i * 0.12}s` }}
                onMouseEnter={() => setHoveredService(i)}
                onMouseLeave={() => setHoveredService(null)}
              >
                <div className="ns-service-icon-wrap" style={{ background: service.gradient }}>
                  <Icon size={28} color="#fff" />
                </div>
                <h3 className="ns-service-title">{service.title}</h3>
                <p className="ns-service-desc">{service.description}</p>
                <ul className="ns-service-highlights">
                  {service.highlights.map((h, j) => (
                    <li key={j}>
                      <CheckCircle2 size={14} color={service.color} />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                {/*
                <div className="ns-service-footer" style={{ color: service.color }}>
                  <span>Learn more</span>
                  <ArrowUpRight size={16} style={{ 
                    transform: isHovered ? 'translate(3px, -3px)' : 'none',
                    transition: 'transform 0.3s ease'
                  }} />
                </div>
                */}
              </div>
            );
          })}
        </div>
      </section>

      {/* ============ FREE TUTORIAL: DATA QUALITY MANAGEMENT ============ */}
      <DataQualityTutorialBanner />

      {/* ============ SOFTWARE & DIGITAL SOLUTIONS ============ */}
      <section id="technology" className="ns-tech-section">
        <SectionHeader
          label="Our Technology"
          title="Software & Digital Solutions"
          subtitle="We don't just work with data — we build the systems that power it. From enterprise platforms to apps and websites, engineered in-house and proven in production."
        />
        <div className="ns-services-grid ns-tech-grid">
          {techSolutions.map((solution, i) => {
            const Icon = solution.icon;
            return (
              <div
                key={i}
                className="ns-service-card ns-reveal-card"
                style={{ '--card-delay': `${i * 0.12}s`, '--tech-accent': solution.color }}
              >
                <div className="ns-service-icon-wrap" style={{ background: solution.gradient }}>
                  <Icon size={28} color="#fff" />
                </div>
                <h3 className="ns-service-title">{solution.title}</h3>
                <p className="ns-service-desc">{solution.description}</p>
                <ul className="ns-service-highlights">
                  {solution.highlights.map((h, j) => (
                    <li key={j}>
                      <CheckCircle2 size={14} color={solution.color} />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                <div className="ns-tech-examples">
                  <span className="ns-tech-examples-label">Built by us</span>
                  <div className="ns-tech-example-chips">
                    {solution.examples.map((ex, k) => (
                      <a
                        key={k}
                        href={ex.href}
                        className="ns-tech-chip"
                        style={{ '--chip-color': solution.color }}
                        {...(ex.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      >
                        {ex.label}
                        <ArrowUpRight size={13} />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section id="enumerators" className="ns-enumerators-section">
        
          <EnumeratorRecruitmentBanner />
      </section>
      {/* ============ IMPACT / STATS ============ */}
      <section id="impact" className="ns-impact-section" ref={impactRef}>
        <SectionHeader
          label="Our Impact"
          title="Numbers That Matter"
          subtitle="Trusted by leading research organizations, governments, and international development partners across Zambia and beyond."
        />
        <div className="ns-impact-grid">
          {impactStats.map((stat, i) => {
            const SIcon = stat.icon;
            return (
              <div key={i} className="ns-impact-card" style={{ '--stat-delay': `${i * 0.1}s` }}>
                <div className="ns-impact-icon">
                  <SIcon size={24} />
                </div>
                <div className="ns-impact-value">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} isVisible={impactVisible} />
                </div>
                <div className="ns-impact-label">{stat.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ============ DATA COLLECTION HIGHLIGHT ============ */}
      <section className="ns-collection-section">
        <div className="ns-collection-inner">
          <div className="ns-collection-visual">
            <div className="ns-collection-card-stack">
              <div className="ns-coll-card ns-coll-card-1">
                <Users size={20} color="#0066FF" />
                <span>Enumerator Training</span>
                <div className="ns-coll-pill">10 Provinces</div>
              </div>
              <div className="ns-coll-card ns-coll-card-2">
                <Clipboard size={20} color="#7C3AED" />
                <span>Field Supervision</span>
                <div className="ns-coll-pill">Real-time QA</div>
              </div>
              <div className="ns-coll-card ns-coll-card-3">
                <Shield size={20} color="#059669" />
                <span>Quality Assurance</span>
                <div className="ns-coll-pill">99.8% Accuracy</div>
              </div>
            </div>
          </div>
          <div className="ns-collection-text">
            <SectionHeader
              label="Field Operations"
              title="Data Collection at Scale"
              subtitle="We recruit, train, and manage enumerator teams for large-scale research projects. From household surveys to targeted institutional assessments — our field teams deliver reliable, high-quality data on time."
              align="left"
            />
            <div className="ns-collection-features">
              <div className="ns-coll-feature">
                <Award size={20} color="#0066FF" />
                <div>
                  <strong>Experienced Teams</strong>
                  <p>Vetted enumerators with training in survey methodology and data ethics</p>
                </div>
              </div>
              <div className="ns-coll-feature">
                <Monitor size={20} color="#7C3AED" />
                <div>
                  <strong>Real-time Monitoring</strong>
                  <p>Live dashboards tracking submissions, quality metrics, and field coverage</p>
                </div>
              </div>
              <div className="ns-coll-feature">
                <Zap size={20} color="#059669" />
                <div>
                  <strong>Rapid Deployment</strong>
                  <p>From kickoff to field in days — not weeks. Standby pools ready nationwide.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* ============ CTA ============ */}
      <section className="ns-cta-section">
        <div className="ns-cta-inner">
          <h2 className="ns-cta-title">Ready to elevate your data?</h2>
          <p className="ns-cta-subtitle">
            Whether you need field data collection, quality assurance, or AI-powered analytics — 
            Noble Syntax has you covered.
          </p>
          <div className="ns-cta-buttons">
            <a href={getAppUrl('/')} className="ns-btn-primary ns-btn-lg">
              Try DataQuad Free <ArrowRight size={18} />
            </a>
            <a href="mailto:support@noblesyntax.com" className="ns-btn-outline ns-btn-lg">
              Contact Us
            </a>
          </div>
        </div>
      </section>
      
      {/* ============ FOOTER ============ */}
      <footer className="ns-footer">
        <div className="ns-footer-inner">
          <div className="ns-footer-brand">
            <img src={logoSource} alt="Noble Syntax" className="ns-footer-logo" />
            <p className="ns-footer-tagline">Data & Technology Consultancy</p>
            <div className="ns-footer-social">
              <a href="https://www.linkedin.com/company/noblesyntaxhq" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="ns-social-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://www.instagram.com/noblesyntaxhq" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="ns-social-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://www.youtube.com/@noblesyntax" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="ns-social-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
          </div>
          <div className="ns-footer-links">
            <div className="ns-footer-col">
              <h4>Product</h4>
              <a href={getAppUrl('/')}>DataQuad</a>
              <button onClick={() => document.getElementById('dataquad')?.scrollIntoView({ behavior: 'smooth' })}>Features</button>
              <a href={getAppUrl('/subscription')}>Pricing</a>
            </div>
            <div className="ns-footer-col">
              <h4>Services</h4>
              <button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}>Data Collection</button>
              <button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}>Quality Management</button>
              <button onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}>Analysis & Visualization</button>
              <button onClick={() => document.getElementById('technology')?.scrollIntoView({ behavior: 'smooth' })}>Software & Digital Solutions</button>
            </div>
            <div className="ns-footer-col">
              <h4>Company</h4>
              <a href="mailto:support@noblesyntax.com">Contact</a>
              <span>Lusaka, Zambia</span>
            </div>
            <div className="ns-footer-col">
              <h4>Legal</h4>
              <a href="/terms">Terms of Service</a>
              <a href="/privacy">Privacy Policy</a>
              <a href="/refund">Refund Policy</a>
            </div>
          </div>
        </div>
        <div className="ns-footer-bottom">
          <p>© {new Date().getFullYear()} Noble Syntax Limited. All rights reserved.</p>
          <div className="ns-footer-bottom-links" style={{ display: 'flex', gap: '24px', marginTop: '8px' }}>
            <a href="/terms" style={{ color: '#9CA3AF', fontSize: '13px', textDecoration: 'none' }}>Terms</a>
            <a href="/privacy" style={{ color: '#9CA3AF', fontSize: '13px', textDecoration: 'none' }}>Privacy</a>
            <a href="/refund" style={{ color: '#9CA3AF', fontSize: '13px', textDecoration: 'none' }}>Refunds</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;